import React, { useEffect, useRef, useState } from "react";

const SmoothCollapse = ({
  summary,
  children,
  defaultOpen = false,
  duration = 300,
  easing = "ease",
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [inlineHeight, setInlineHeight] = useState("0px"); // "0px" | number px | "auto"
  const contentRef = useRef(null);
  const roRef = useRef(null);
  const animatingRef = useRef(false);

  const toggle = () => setIsOpen((v) => !v);

  // 開閉トグル時のアニメーション
  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;

    const current = el.getBoundingClientRect().height;
    const target = isOpen ? el.scrollHeight : 0;

    // すでに auto のときは現在値を固定してからアニメ
    if (isOpen && inlineHeight === "auto") {
      setInlineHeight(`${current}px`);
    }

    // 次フレームで目的地へ
    requestAnimationFrame(() => {
      animatingRef.current = true;
      setInlineHeight(`${target}px`);
    });

    // アニメ終了後に auto に戻す（開いた時のみ）
    const onEnd = () => {
      if (!animatingRef.current) return;
      animatingRef.current = false;
      if (isOpen) setInlineHeight("auto");
    };
    el.addEventListener("transitionend", onEnd);
    return () => el.removeEventListener("transitionend", onEnd);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // 中身の高さ変化を監視（開いている間は追随）
  useEffect(() => {
    const el = contentRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    roRef.current?.disconnect();

    const ro = new ResizeObserver(() => {
      if (!isOpen) return;
      // auto のままでもOKだが、行追加中にカクつく場合は一度 px にして追随させる
      const h = el.scrollHeight;
      setInlineHeight(`${h}px`);
      // 次フレームで再び auto（連続追加にも強い）
      requestAnimationFrame(() => setInlineHeight("auto"));
    });

    ro.observe(el);
    roRef.current = ro;
    return () => ro.disconnect();
  }, [isOpen]);

  return (
    <div style={{ marginBottom: 8 }}>
      <button
        type="button"
        onClick={toggle}
        style={{
          cursor: "pointer",
          fontWeight: "bold",
          marginBottom: 4,
          display: "flex",
          alignItems: "center",
          gap: 4,
          userSelect: "none",
          background: "transparent",
          border: 0,
          padding: 0,
          fontSize: "16px",
        }}
        aria-expanded={isOpen}
      >
        <span
          style={{
            display: "inline-block",
            transform: isOpen ? "rotate(90deg)" : "rotate(0deg)",
            transition: `transform ${duration}ms ${easing}`,
            fontSize: "16px",
          }}
        >
          ▶️
        </span>
        {summary}
      </button>

      <div
        ref={contentRef}
        style={{
          overflow: "hidden",
          height: inlineHeight,
          transition: `height ${duration}ms ${easing}`,
          willChange: "height",
        }}
      >
        {children}
      </div>
    </div>
  );
};

export default SmoothCollapse;